/** Shown instead of the review wizard when a run failed due to upstream infrastructure. */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { faArrowLeft, faTriangleExclamation } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import type { AgentRun } from "./types";
import { ReviewCaseHeader } from "./ReviewCaseHeader";
import { OutcomePhase } from "./OutcomePhase";
import { OrchestrationPhase } from "./OrchestrationPhase";
import { splitRunPhases } from "./phases";

interface InfrastructureFailurePanelProps {
  run: AgentRun;
  caseLabel?: string;
  onDone: () => void;
}

export function InfrastructureFailurePanel({ run, caseLabel, onDone }: InfrastructureFailurePanelProps) {
  const phases = splitRunPhases(run);
  const agentName = run.agent_name?.trim() || "Agent";
  const reason = run.infrastructure_failure_reason?.replace(/_/g, " ") ?? "upstream service unavailable";

  return (
    <div className={clsx("flex flex-col h-full w-full p-4", WORKBENCH_ROOT_TEXT_CLASS)} data-testid="agent-review-infra-failure">
      <div className={clsx("relative flex-1 min-h-0 flex flex-col rounded-2xl", WORKBENCH_SURFACE_CLASS)}>
        <div className="px-6 pt-4 pb-3 flex-shrink-0">
          <ReviewCaseHeader
            caseLabel={caseLabel ?? run.subject_label}
            plannrAccountUuid={run.plannr_account_uuid}
            taskName={run.task_name}
            agentName={agentName !== "Agent" ? agentName : undefined}
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0 space-y-6">
          <div className="rounded-xl border border-rose-500/30 bg-rose-950/30 px-4 py-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 text-rose-300" />
              <div>
                <p className="text-sm font-medium text-rose-100">Critical infrastructure failure</p>
                <p className="mt-1 text-sm text-rose-100/80">
                  This run did not complete because the {reason}. It has been recorded automatically and
                  does not need human review or Ace testing.
                </p>
              </div>
            </div>
          </div>

          {phases.orchestration.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                What {agentName} did before the failure
              </p>
              <OrchestrationPhase run={run} steps={phases.orchestration} outcome={phases.outcome} />
            </div>
          ) : null}

          {phases.outcome ? (
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Outcome</p>
              <OutcomePhase run={run} steps={phases.orchestration} step={phases.outcome} agentName={agentName} />
            </div>
          ) : null}
        </div>

        <div className="border-t border-slate-800/80 px-6 py-4 flex-shrink-0">
          <button
            type="button"
            onClick={onDone}
            className="inline-flex items-center gap-2 text-sm text-indigo-300 hover:text-indigo-200 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to queue
          </button>
        </div>
      </div>
    </div>
  );
}
